import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistoryDateRangeStyles {
    searchFieldWrapper?: SearchFieldWrapperStyles;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    fromGridItem?: GridItemProps;
    toGridItem?: GridItemProps;
}

export const dateRangeStyles: OrderHistoryDateRangeStyles = {
    datePickersWrapper: {
        css: css`
            display: flex;
        `,
    },

    fromGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            max-width: fit-content !important;
            > div {
                > div[class^="FormFieldStyle"] {
                    > div[class^="FormInputWrapper"] {
                        margin-top: 12px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                        right: unset;
                                    }
                                }
                            `,
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                        right: unset;
                                    }
                                }
                            `,
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                        right: unset;
                                    }
                                }
                            `,
                            css`
                                margin-left: -10px;
                                > div[class^="FormFieldStyle"] {
                                    padding-right: 0 !important;
                                }
                            `,
                            null,
                        ],
                        "max",
                    )}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 20px !important;
                        `,
                        css`
                            margin-bottom: 20px !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    toGridItem: {
        width: [12, 6, 6, 7, 6],
        css: css`
            max-width: fit-content !important;
            > div {
                position: relative;
                > div[class^="FormFieldStyle"] {
                    > label {
                        z-index: 1;
                        margin-left: 20px;
                        position: inherit;
                        background: none;
                        line-height: 25px;
                        top: 2px;
                    }
                    > div[class^="FormInputWrapper"] {
                        margin-top: -12px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                    }
                                }
                            `,
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                    }
                                }
                            `,
                            css`
                                .react-datetime-picker {
                                    div[class^="react-calendar"] {
                                        bottom: 35px;
                                        top: auto;
                                    }
                                }
                            `,
                            css`
                                margin-left: 10px;
                                > div[class^="FormFieldStyle"] {
                                    padding-right: 0 !important;
                                }
                            `,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
};

const styles = dateRangeStyles;
const OrderHistorySearchFieldDateRange: React.FunctionComponent<Props> = props => {
    const { parameter, updateSearchFields } = props;
    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        const loadParameter = {
            ...parameter,
            fromDate: convertDateToDateOnlyString(selectedDay),
        };
        updateSearchFields(loadParameter);
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        const loadParameter = {
            ...parameter,
            toDate: convertDateToDateOnlyString(selectedDay),
        };
        updateSearchFields(loadParameter);
    };

    const fromDate = convertDateOnlyStringToDate(parameter.fromDate);
    const toDate = convertDateOnlyStringToDate(parameter.toDate);

    return (
        <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
            <StyledWrapper {...styles.datePickersWrapper} data-test-selector="orderHistory_filterDateRange">
                <GridItem {...styles.fromGridItem}>
                    <DatePicker
                        data-test-selector="orderHistory_filterFromDate"
                        {...styles.fromDate}
                        label={translate("Start Date")}
                        selectedDay={fromDate}
                        onDayChange={fromDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            maxDate: toDate,
                            ...styles.fromDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
                <GridItem {...styles.toGridItem}>
                    <DatePicker
                        data-test-selector="orderHistory_filterToDate"
                        {...styles.toDate}
                        label={translate("End Date")}
                        selectedDay={toDate}
                        onDayChange={toDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: fromDate,
                            ...styles.toDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
            </StyledWrapper>
        </SearchFieldWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldDateRange),
    definition: {
        group: "Order History",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        displayName: "Date Range",
    },
};

export default widgetModule;
